import { get } from 'lodash';
import Grid from '@lendi-ui/grid';

import { FIELD_PATHS, FIELD_VALUES } from '@funnels/constants';
import { useConstructionCalculator } from '@funnels/hooks';
import { FormValues } from '@funnels/types';
import { getTotalConstructionCosts } from '@funnels/utils';

import {
  BuildPriceReadOnlyView,
  DepositAmountReadOnlyView,
  IsSellingExistingPropertyReadOnlyView,
  LandValueReadOnlyView,
  PropertyAddressReadOnlyView,
  PropertyStatusReadOnlyView,
  PropertyUseReadOnlyView,
  PropertyValueReadOnlyView,
  TotalConstructionCostsReadOnlyView,
} from '../../../Questions';
import { Divider, PaddedGridUnit } from '../_common/style';

interface ConstructionLoanLayoutProps {
  index: number;
  currentValues: FormValues;
}

export const ConstructionLoanLayout = (props: ConstructionLoanLayoutProps) => {
  const { currentValues, index } = props;
  const address = get(currentValues, FIELD_PATHS.ASSET_ADDRESS(index));
  const propertyValue = get(currentValues, FIELD_PATHS.ASSET_ESTIMATED_VALUE(index));
  const landValue = get(currentValues, FIELD_PATHS.ASSET_LAND_VALUE(index));
  const buildPrice = get(currentValues, FIELD_PATHS.ASSET_BUILD_PRICE_AMOUNT(index));
  const deposit = get(currentValues, FIELD_PATHS.APPLICATION_CONTRIBUTION_AMOUNT(0, 0));
  const propertyStatus = get(currentValues, FIELD_PATHS.ASSET_PROPERTY_STATUS(index));
  const primaryPurpose = get(currentValues, FIELD_PATHS.ASSET_PRIMARY_PURPOSE(index));
  const isSellingExistingProperty = get(currentValues, FIELD_PATHS.APPLICATION_IS_SELLING_EXISTING_PROPERTY(0));

  return (
    <Grid gutter="nil">
      {index > 0 && (
        <PaddedGridUnit size={1}>
          <Divider />
        </PaddedGridUnit>
      )}
      <PaddedGridUnit size={1}>
        <PropertyAddressReadOnlyView address={address} title={'Address'} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <PropertyValueReadOnlyView value={propertyValue} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <DepositAmountReadOnlyView value={deposit} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1}>
        <LandValueReadOnlyView value={landValue} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1}>
        <BuildPriceReadOnlyView value={buildPrice} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <IsSellingExistingPropertyReadOnlyView value={isSellingExistingProperty} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <PropertyUseReadOnlyView value={primaryPurpose} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <PropertyStatusReadOnlyView value={propertyStatus} />
      </PaddedGridUnit>
    </Grid>
  );
};

interface NewPurchaseLayoutProps {
  index: number;
  // Property fields
  currentValues: FormValues;
}

export const NewPurchaseLayout = (props: NewPurchaseLayoutProps) => {
  const { currentValues, index } = props;
  const { isConstruction } = useConstructionCalculator(index, currentValues);
  const address = get(currentValues, FIELD_PATHS.ASSET_ADDRESS(index));
  const propertyValue = get(currentValues, FIELD_PATHS.ASSET_ESTIMATED_VALUE(index));
  const landValue = get(currentValues, FIELD_PATHS.ASSET_LAND_VALUE(index));
  const deposit = get(currentValues, FIELD_PATHS.APPLICATION_CONTRIBUTION_AMOUNT(0, 0));
  const propertyStatus = get(currentValues, FIELD_PATHS.ASSET_PROPERTY_STATUS(index));
  const primaryPurpose = get(currentValues, FIELD_PATHS.ASSET_PRIMARY_PURPOSE(index));
  const isSellingExistingProperty = get(currentValues, FIELD_PATHS.APPLICATION_IS_SELLING_EXISTING_PROPERTY(0));
  const totalConstructionCosts = getTotalConstructionCosts(currentValues, index);

  return (
    <Grid gutter="nil">
      {index > 0 && (
        <PaddedGridUnit size={1}>
          <Divider />
        </PaddedGridUnit>
      )}
      <PaddedGridUnit size={1}>
        <PropertyAddressReadOnlyView address={address} title={'Address'} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <PropertyValueReadOnlyView value={propertyValue} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <DepositAmountReadOnlyView value={deposit} />
      </PaddedGridUnit>
      {isConstruction && (
        <>
          <PaddedGridUnit size={1 / 2}>
            <LandValueReadOnlyView value={landValue} />
          </PaddedGridUnit>
          <PaddedGridUnit size={1 / 2}>
            <TotalConstructionCostsReadOnlyView value={totalConstructionCosts} />
          </PaddedGridUnit>
        </>
      )}
      <PaddedGridUnit size={1 / 2}>
        <PropertyUseReadOnlyView value={primaryPurpose} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <IsSellingExistingPropertyReadOnlyView value={isSellingExistingProperty} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <PropertyStatusReadOnlyView value={propertyStatus} />
      </PaddedGridUnit>
    </Grid>
  );
};

interface PreapprovalLayoutProps {
  index: number;
  // Property fields
  currentValues: FormValues;
}

export const PreapprovalLayout = (props: PreapprovalLayoutProps) => {
  const { currentValues, index } = props;
  const address = get(currentValues, FIELD_PATHS.ASSET_ADDRESS(index));
  const propertyValue = get(currentValues, FIELD_PATHS.ASSET_ESTIMATED_VALUE(index));
  const deposit = get(currentValues, FIELD_PATHS.APPLICATION_CONTRIBUTION_AMOUNT(0, 0));
  const landValue = get(currentValues, FIELD_PATHS.ASSET_LAND_VALUE(index));
  const buildPrice = get(currentValues, FIELD_PATHS.ASSET_BUILD_PRICE_AMOUNT(index));
  const propertyStatus = get(currentValues, FIELD_PATHS.ASSET_PROPERTY_STATUS(index));
  const propertyType = get(currentValues, FIELD_PATHS.ASSET_PROPERTY_TYPE(index));
  const primaryPurpose = get(currentValues, FIELD_PATHS.ASSET_PRIMARY_PURPOSE(index));
  const isConstructionLoan = propertyStatus === FIELD_VALUES.VACANT_LAND && propertyType !== FIELD_VALUES.LAND;

  return (
    <Grid gutter="nil">
      {index > 0 && (
        <PaddedGridUnit size={1}>
          <Divider />
        </PaddedGridUnit>
      )}
      <PaddedGridUnit size={1}>
        <PropertyAddressReadOnlyView address={address} title={'Address'} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <PropertyValueReadOnlyView value={propertyValue} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <DepositAmountReadOnlyView value={deposit} />
      </PaddedGridUnit>
      {isConstructionLoan && (
        <>
          <PaddedGridUnit size={1}>
            <LandValueReadOnlyView value={landValue} />
          </PaddedGridUnit>
          <PaddedGridUnit size={1}>
            <BuildPriceReadOnlyView value={buildPrice} />
          </PaddedGridUnit>
        </>
      )}
      <PaddedGridUnit size={1 / 2}>
        <PropertyUseReadOnlyView value={primaryPurpose} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <PropertyStatusReadOnlyView value={propertyStatus} />
      </PaddedGridUnit>
    </Grid>
  );
};

interface RefinanceLayoutProps {
  index: number;
  // Property fields
  currentValues: FormValues;
}

export const RefinanceLayout = (props: RefinanceLayoutProps) => {
  const { currentValues, index } = props;
  const { isConstruction } = useConstructionCalculator(index, currentValues);
  const address = get(currentValues, FIELD_PATHS.ASSET_ADDRESS(index));
  const propertyValue = get(currentValues, FIELD_PATHS.ASSET_ESTIMATED_VALUE(index));
  const landValue = get(currentValues, FIELD_PATHS.ASSET_LAND_VALUE(index));
  const primaryPurpose = get(currentValues, FIELD_PATHS.ASSET_PRIMARY_PURPOSE(index));
  const totalConstructionCosts = getTotalConstructionCosts(currentValues, index);

  return (
    <Grid gutter="nil">
      {index > 0 && (
        <PaddedGridUnit size={1}>
          <Divider />
        </PaddedGridUnit>
      )}
      <PaddedGridUnit size={1}>
        <PropertyAddressReadOnlyView address={address} title={'Address'} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <PropertyValueReadOnlyView value={propertyValue} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <PropertyUseReadOnlyView value={primaryPurpose} />
      </PaddedGridUnit>
      {isConstruction && (
        <>
          <PaddedGridUnit size={1 / 2}>
            <LandValueReadOnlyView title={'Land/Property value'} value={landValue} />
          </PaddedGridUnit>
          <PaddedGridUnit size={1 / 2}>
            <TotalConstructionCostsReadOnlyView value={totalConstructionCosts} />
          </PaddedGridUnit>
        </>
      )}
    </Grid>
  );
};
